import { useAuthStore } from './auth';

/**
 * WhatsApp magic-link bootstrap (Phase 12 Commit 2).
 *
 * GET /wa/:token on the backend redirects here with a refresh token in the
 * URL *fragment* (`#wa_rt=...`) -- a fragment because it's never sent to
 * the server on the next request and doesn't show up in access logs or
 * Referer headers the way a query param would. This exchanges it at the
 * same POST /auth/refresh endpoint an expired session already uses, which
 * hands back a fresh access token AND the full user profile in one call,
 * then clears the fragment so it can't linger in history or get shared by
 * accident (it's still a live token until this exchange consumes it).
 *
 * Awaited from main.tsx before the app renders, so every route guard sees
 * a fully-populated session on first paint rather than a flash of
 * logged-out state.
 */
export async function runMagicLinkBootstrap(): Promise<void> {
  const hash = window.location.hash;
  if (!hash.includes('wa_rt=')) return;

  const params = new URLSearchParams(hash.slice(1));
  const refreshToken = params.get('wa_rt');
  // Strip the fragment immediately, whether or not the exchange below
  // succeeds -- it's a one-time credential either way.
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  if (!refreshToken) return;

  try {
    const res = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    const env = await res.json();
    if (!env.ok || !env.data) return; // link already used/expired -- land on the page logged out, same as any dead link
    useAuthStore.getState().setSession(env.data.access_token, env.data.refresh_token, env.data.user);
  } catch {
    // Network hiccup -- fall through logged out rather than blocking the app from ever rendering.
  }
}
